// Wheel-style weight entry (lb + oz).
// Minimal native wheel: two scroll columns with snap. Simple, accessible, no deps.

import { useState } from "react";
import { lbOzToGrams } from "../lib/events";
import { useT } from "../lib/LocaleContext";

type Props = {
  open: boolean;
  initialLb?: number;
  initialOz?: number;
  onSubmit: (grams: number) => void;
  onClose: () => void;
};

const LB_RANGE = Array.from({ length: 21 }, (_, i) => i + 5); // 5–25
const OZ_RANGE = [0, 2, 4, 6, 8, 10, 12, 14];

export function WeightEntry({ open, initialLb = 11, initialOz = 0, onSubmit, onClose }: Props) {
  const [lb, setLb] = useState(initialLb);
  const [oz, setOz] = useState(initialOz);
  const t = useT();

  if (!open) return null;
  const grams = lbOzToGrams(lb, oz);

  return (
    <div style={styles.backdrop} onClick={onClose}>
      <div style={styles.sheet} onClick={(e) => e.stopPropagation()}>
        <div style={styles.handle} />
        <h2 style={styles.title}>{t.weightEntry.title}</h2>
        <div style={styles.row}>
          <Wheel label={t.weightEntry.lb} range={LB_RANGE} value={lb} onChange={setLb} />
          <Wheel label={t.weightEntry.oz} range={OZ_RANGE} value={oz} onChange={setOz} />
        </div>
        <div style={styles.summary}>
          <span className="bh-num" style={styles.summaryBig}>
            {t.weightEntry.lbOz(lb, oz)}
          </span>
          <span className="bh-num" style={styles.summarySub}>
            {t.weightEntry.approxKg((grams / 1000).toFixed(2))}
          </span>
        </div>
        <div style={styles.actions}>
          <button style={styles.ghost} onClick={onClose}>
            {t.weightEntry.cancel}
          </button>
          <button
            style={styles.primary}
            onClick={() => {
              onSubmit(grams);
              onClose();
            }}
          >
            {t.weightEntry.save}
          </button>
        </div>
      </div>
    </div>
  );
}

function Wheel({ label, range, value, onChange }: { label: string; range: number[]; value: number; onChange: (n: number) => void }) {
  return (
    <div style={styles.wheel}>
      <div style={styles.wheelLabel}>{label}</div>
      <div style={styles.wheelList}>
        {range.map((n) => {
          const selected = n === value;
          return (
            <button
              key={n}
              style={{
                ...styles.wheelItem,
                color: selected ? "var(--bh-ink)" : "var(--bh-ink-3)",
                fontWeight: selected ? 700 : 400,
                background: selected ? "var(--bh-surface-press)" : "transparent",
              }}
              onClick={() => onChange(n)}
            >
              {n}
            </button>
          );
        })}
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  backdrop: {
    position: "fixed",
    inset: 0,
    background: "oklch(0 0 0 / 0.55)",
    zIndex: 110,
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "center",
  },
  sheet: {
    background: "var(--bh-surface)",
    width: "100%",
    maxWidth: 520,
    borderTopLeftRadius: 28,
    borderTopRightRadius: 28,
    padding: "10px 16px max(28px, env(safe-area-inset-bottom))",
    boxShadow: "0 -20px 60px oklch(0 0 0 / 0.4)",
  },
  handle: {
    width: 40,
    height: 4,
    background: "var(--bh-divider)",
    borderRadius: 2,
    margin: "0 auto 14px",
  },
  title: { fontSize: 18, fontWeight: 600, margin: "0 0 12px", color: "var(--bh-ink)" },
  row: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 },
  wheel: { background: "var(--bh-surface-2)", borderRadius: 16, padding: 12 },
  wheelLabel: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: "var(--bh-ink-3)",
    marginBottom: 8,
    textAlign: "center",
  },
  wheelList: {
    height: 180,
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    gap: 4,
    scrollbarWidth: "none",
  },
  wheelItem: {
    appearance: "none",
    border: "none",
    fontFamily: "var(--bh-font-mono)",
    fontSize: 22,
    padding: "8px 12px",
    cursor: "pointer",
    borderRadius: 10,
    textAlign: "center",
  },
  summary: { display: "flex", alignItems: "baseline", gap: 8, padding: "10px 4px 14px" },
  summaryBig: { fontSize: 22, fontWeight: 600, color: "var(--bh-ink)" },
  summarySub: { fontSize: 14, color: "var(--bh-ink-3)" },
  actions: { display: "flex", gap: 8 },
  primary: {
    flex: 1,
    background: "var(--bh-feed)",
    color: "oklch(0.2 0.04 70)",
    border: "none",
    padding: "14px",
    fontSize: 16,
    fontWeight: 600,
    borderRadius: 14,
    cursor: "pointer",
    minHeight: 56,
  },
  ghost: {
    flex: 1,
    background: "var(--bh-surface-2)",
    color: "var(--bh-ink-2)",
    border: "none",
    padding: "14px",
    fontSize: 16,
    fontWeight: 500,
    borderRadius: 14,
    cursor: "pointer",
    minHeight: 56,
  },
};
